// The nav tree, in one place. SideNav.vue renders it; header.vue uses it to resolve the
// current page title in sidebar mode. Gates are keys on the object returned by
// computeNavPermissions() — same role rules header.vue has always applied, lifted verbatim.
//
// Item shapes:
//   { label, to, icon, gate? }                         internal route
//   { label, href, icon, gate?, external: true }       external link (new tab)
//   { label, icon, gate?, group: true, children: [...] }
//   children may include { section: "Heading" } dividers (used by swiftComply and Insights)

import { INSIGHT_PAGES, insightPath, isInsightsEnabled, insightGates, canSeeInsightPage } from "@/helpers/insights/insightPages.js"
import { INSIGHT_COMPONENTS } from "@/router/insightRoutes.js"
import { isConnectEnabled } from "@/helpers/connect/connectFeatures.js"

function getRolesFromStorage(key) {
  let raw = null
  try {
    raw = window?.localStorage?.getItem(key)
  } catch (e) {}
  if (!raw) return []

  const toList = v =>
    String(v || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(x => String(x || "").trim()).filter(Boolean)
    return toList(parsed)
  } catch {
    return toList(raw)
  }
}

function rolesInclude(roles, role) {
  const target = String(role || "").toLowerCase().trim()
  return (roles || []).map(x => String(x || "").toLowerCase().trim()).includes(target)
}

export function computeNavPermissions() {
  const roles = getRolesFromStorage("active_user_roles")
  const isCorp = rolesInclude(roles, "Corp")
  const canViewRepricedLocks = rolesInclude(roles, "Compensation Admin")
  // Pricing Comparison has its own role: Compensation Admin reaches HR, payroll and IT too.
  // Visibility only; the API checks the same role on every call.
  const canViewPricingComparison = rolesInclude(roles, "Pricing Admin")

  // A Branch or Division Manager gets ONE item in swiftLocks: Revenue Analysis, scoped by the
  // API — a branch manager to their own branch, a division manager to every branch in their
  // division. This decides menu VISIBILITY only; the scope itself is enforced server-side
  // (SwiftAppAIService.GetRevenueAlertAnalysis), because a scope a browser could edit is not a
  // scope.
  //
  // A division manager's authority comes from the Reporting division_managers roster, not from
  // this role — so the API serves them whether or not the role is assigned. The role is what
  // puts the link in front of them.
  const canViewRevenueAnalysis = canViewRepricedLocks
    || rolesInclude(roles, "Branch Manager")
    || rolesInclude(roles, "Division Manager")

  // Insights (ported from CGS2022): behind the insights flag, then one gate per page, from the
  // CGS menu rules. See helpers/insights/insightPages.js for who sees what, and why.
  const insightsOn = isInsightsEnabled()
  const gates = insightGates(roles)
  const insightPerms = {}
  INSIGHT_PAGES.forEach(p => { insightPerms[insightGateKey(p.slug)] = insightsOn && canSeeInsightPage(p, gates) })

  return {
    ...insightPerms,
    canViewInsights: Object.values(insightPerms).some(Boolean),
    isCorp,
    isPostClosing: rolesInclude(roles, "Post Closer") || isCorp,
    canViewRepricedLocks,
    canViewRevenueAnalysis,
    canViewPricingComparison,
    // The group opens for anyone with at least one item in it. myLocks is no longer one of them:
    // it is its own top-level item, open to everyone.
    canViewLocks: canViewRepricedLocks || canViewRevenueAnalysis || canViewPricingComparison,
    canViewHmda: isCorp || rolesInclude(roles, "HMDA"),
    // Connect (helpers/connect/connectFeatures.js): every user once the flag is on. The API scopes
    // the contacts to the signed-in user, so this is visibility only.
    canViewContacts: isConnectEnabled(),
    // swiftAnalyze. Mirror any change in header.vue applyNavPermissions().
    isAdmin: rolesInclude(roles, "Admin")
  }
}

export function applicationAdminLink() {
  return `${process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT}/applicationAdmin`
}

// "Tools" (was "Me"): the signed-in LO's profile, website and notifications, plus Application
// Admin for corp users. Admin is gated per child, so non-corp users just see the first three.
const websiteNavItem = {
  label: "Tools",
  icon: "fa-light fa-toolbox",
  group: true,
  children: [
    { label: "My Profile", to: "/my-profile" },
    { label: "My Website", to: "/LORedirect" },
    { label: "Notification Management", to: "/NotificationManagement" },
    { label: "Application Admin", href: applicationAdminLink, gate: "isCorp", external: true }
  ]
}

export function insightGateKey(slug) {
  return `insight:${slug}`
}

// The Insights group: one heading per section, one link per ported page, each link gated on its
// own page rule. Headings that are left with no links are dropped by filterByPermissions.
const insightsNavItem = {
  label: "Insights",
  icon: "fa-light fa-chart-mixed",
  gate: "canViewInsights",
  group: true,
  children: INSIGHT_PAGES.filter(p => INSIGHT_COMPONENTS[p.slug]).reduce((out, p, i, arr) => {
    if (i === 0 || arr[i - 1].section !== p.section) out.push({ section: p.section })
    out.push({ label: p.label, to: insightPath(p.slug), gate: insightGateKey(p.slug) })
    return out
  }, [])
}

export const NAV_ITEMS = [
  { label: "Command Center", to: "/", icon: "fa-light fa-grid-2" },
  // CGS contacts as a list or a status pipeline, and the contact page. No marketing. Behind the
  // connect flag (canViewContacts).
  { label: "Contacts", to: "/contacts", icon: "fa-light fa-address-card", gate: "canViewContacts" },
  { label: "Pipeline", to: "/Processing", icon: "fa-light fa-table-cells-large" },
  { label: "Applications", to: "/Applications", icon: "fa-light fa-users" },
  // Scenario Mode — no loanId. Opens in the same tab now (was: external tab) so it behaves
  // like every other internal nav item; the per-loan button in the Pipeline grid instead opens
  // as a left-side flyout so users don’t lose their place on the grid (see ProcessingV2.vue).
  { label: "Pricing Engine", to: "/polly-pricer", icon: "fa-light fa-calculator" },
  // Ungated: every user's own locks, scoped server-side (GetMyLocks). Its own item rather than a
  // swiftLocks child, so it sits beside the Pricing Engine it works with.
  { label: "myLocks", to: "/my-locks", icon: "fa-light fa-lock-keyhole" },
  // Ungated like myLocks: every LO's own prequals, widened to their branch or everyone server side.
  { label: "swiftPrequal", to: "/swift-prequal", icon: "fa-light fa-file-lines" },
  { label: "Leaderboard", to: "/Leaderboard", icon: "fa-light fa-trophy" },
  // Ported from CGS2022. Ungated — every LO checks turn times.
  { label: "Turn Times", to: "/turn-times", icon: "fa-light fa-stopwatch" },
  // LoRedirect.vue — the LO picks and manages their own marketing site. No loanId, no
  // role gate: every LO manages their own. Route is /LORedirect; isPathActive lowercases
  // both sides, so the /loredirect URL matches too.
  websiteNavItem,
  { label: "swiftPC", to: "/PostCloseConnect", icon: "fa-light fa-desktop", gate: "isPostClosing" },
  {
    label: "swiftUW",
    icon: "fa-light fa-building",
    gate: "isCorp",
    group: true,
    children: [
      { label: "Condition Insights", to: "/Insights" },
      { label: "Appraisal Review", to: "/AppraisalReview" }
    ]
  },
  { label: "swiftAnalyze", to: "/swift-analyze", icon: "fa-light fa-file-magnifying-glass", gate: "isAdmin" },
  // Purchase Agreement Import (9189) moved to SWIFT SPA (/PurchaseAgreement), which is already on one
  // loan. Hidden here; the route still works by URL. Uncomment with header.vue to bring it back.
  // { label: "PA Import", to: "/purchase-agreement-import", icon: "fa-light fa-file-import", gate: "isAdmin" },
  {
    label: "swiftLocks",
    icon: "fa-light fa-lock",
    gate: "canViewLocks",
    group: true,
    // Children ARE gate-filtered now (filterByPermissions recurses), because a Branch Manager
    // may open this group for Revenue Analysis alone and must not be handed the lock desk's
    // pages with it. A child with no gate stays visible to anyone who can see the group.
    children: [
      { label: "Work Queue", to: "/lock-desk-queue", gate: "canViewRepricedLocks" },
      // Reporting on the revenue alerts the Work Queue holds — the alerts themselves are
      // worked from the queue, this reads them back.
      { label: "Revenue Analysis", to: "/revenue-analysis", gate: "canViewRevenueAnalysis" },
      // How long locks sit unvalidated. The page itself is ConcessionsAdmin-only (server-side);
      // that role is not in active_user_roles, so the link rides the lock desk gate like Concessions.
      { label: "Validations", to: "/validations", gate: "canViewRepricedLocks" },
      { label: "Lock Reprice", to: "/reprice", gate: "canViewRepricedLocks" },
      { label: "Lock Extensions", to: "/extensions", gate: "canViewRepricedLocks" },
      { label: "Extension Request", to: "/lock-extension-request", gate: "canViewRepricedLocks" },
      { label: "Price Sheet", to: "/loan-price-sheet", gate: "canViewRepricedLocks" },
      // Investor prices side by side for one made-up scenario, against Village Capital's version.
      // The page itself is Pricing Admin only server-side as well.
      { label: "Pricing Comparison", to: "/pricing-comparison", gate: "canViewPricingComparison" },
      // Moved in here from the top level, where it was ungated. It rides the lock desk gate,
      // and the page still self-gates off the Polly roster on top of that.
      { label: "Concessions", to: "/concessions", gate: "canViewRepricedLocks" }
    ]
  },
  insightsNavItem,
  {
    // Ungated on purpose: these are generic arithmetic tools with no loan data behind them,
    // so there is nothing here to scope by role. More children get added as we build them.
    label: "Calculators",
    icon: "fa-light fa-abacus",
    group: true,
    children: [
      { label: "Payment Calculator", to: "/calculators/payment" },
      { label: "Buydown Calculator", to: "/calculators/buydown" },
      { label: "Debt Consolidation", to: "/calculators/debt-consolidation" },
      { label: "Cash-Out Refi Scenario", to: "/calculators/cash-out-refi" },
      { label: "Scenario Builder", to: "/calculators/scenario-builder" }
    ]
  },
  {
    label: "swiftComply",
    icon: "fa-light fa-shield-check",
    gate: "canViewHmda",
    group: true,
    children: [
      { section: "Disclosures" },
      { label: "ICD", to: "/ICD" },
      { label: "Disclosure Audit", to: "/disclosure-audit" },
      { section: "Loan Status" },
      { label: "Loan Status", to: "/hmda-withdrawals" },
      { section: "Insuring" },
      { label: "Closing Packages", to: "/ai-insuring" },
      { label: "Upload FHA MIP", to: "/Insuring" },
      { label: "FHA MIP Payments", to: "/fha-mip-payments" },
      { section: "Post Consummation" },
      { label: "PCCD", to: "/pccd" }
    ]
  },
  {
    label: "swiftAccounting",
    icon: "fa-light fa-calculator-simple",
    gate: "canViewAccounting",
    group: true,
    children: [
      { section: "Loan Vision" },
      { label: "Loans Funded", to: "/loans-funded" }
    ]
  }
]

// Routes are case-insensitive in vue-router; compare lowercase. "/" must be exact or it
// would light up on every page.
export function isPathActive(routePath, to) {
  const path = String(routePath || "").toLowerCase()
  const target = String(to || "").toLowerCase()
  if (target === "/") return path === "/"
  return path === target || path.startsWith(target + "/")
}

// Filters the tree, groups included. A group's children are filtered too, and a group left
// with nothing navigable is dropped rather than rendered as an empty menu — that is what lets a
// gate on a single child stand on its own (see swiftLocks / Revenue Analysis).
//
// Returns new objects: NAV_ITEMS is module state shared by both renderers and is never mutated.
export function filterByPermissions(items, perms) {
  const allowed = i => !i.gate || perms[i.gate]

  return items.filter(allowed).map(item => {
    if (!item.group) return item

    // A section heading survives only when at least one link follows it before the next heading.
    // Ungated groups (swiftComply) are unaffected. This matters for Insights, where per-page
    // gates can empty a whole section.
    const children = (item.children || []).filter(allowed).filter((c, i, arr) => {
      if (!c.section) return true
      for (let j = i + 1; j < arr.length && !arr[j].section; j++) if (arr[j].to || arr[j].href) return true
      return false
    })

    // Section headings carry no gate, so a group can survive on headings alone. Require a real
    // destination before keeping it.
    return children.some(c => c.to || c.href) ? { ...item, children } : null
  }).filter(Boolean)
}

// Page title for the top bar: deepest matching leaf, with its group's icon as fallback.
export function resolvePageTitle(routePath) {
  for (const item of NAV_ITEMS) {
    if (item.group) {
      const hit = item.children.find(c => c.to && isPathActive(routePath, c.to))
      if (hit) return { label: hit.label, parent: item.label, icon: item.icon }
    } else if (item.to && isPathActive(routePath, item.to)) {
      return { label: item.label, parent: null, icon: item.icon }
    }
  }
  return null
}